import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PROTOCOL_CATEGORIES, CHART_TIMEFRAMES } from '../../utils/constants';
import { calculateWellnessScore, SYMPTOM_CATALOG, DEFAULT_ENABLED_SYMPTOMS } from '../../utils/wellness';
import ComplianceWellnessChart from '../../components/charts/ComplianceWellnessChart';
import CategoryBreakdownChart from '../../components/charts/CategoryBreakdownChart';
import WellnessTrendsChart from '../../components/charts/WellnessTrendsChart';
import './MyProgress.css';

export default function MyProgress() {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [timeframe, setTimeframe] = useState(30);
  const [loading, setLoading] = useState(true);

  // Data
  const [dailyData, setDailyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [wellnessData, setWellnessData] = useState([]);
  const [protocolEvents, setProtocolEvents] = useState([]);

  useEffect(() => {
    if (user) loadClient();
  }, [user]);

  useEffect(() => {
    if (client) loadChartData();
  }, [client, timeframe]);

  async function loadClient() {
    const { data } = await supabase
      .from('clients')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    setClient(data);
    if (!data) setLoading(false);
  }

  async function loadChartData() {
    setLoading(true);

    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    if (timeframe > 0) {
      startDate.setDate(startDate.getDate() - timeframe);
    } else {
      startDate.setTime(new Date(client.start_date || client.created_at).getTime());
    }
    const startStr = startDate.toISOString().split('T')[0];

    // Fetch all data in parallel
    const [itemsRes, checkoffsRes, logsRes, eventsRes] = await Promise.all([
      supabase
        .from('protocol_items')
        .select('*')
        .eq('client_id', client.id)
        .eq('is_active', true),
      supabase
        .from('checkoffs')
        .select('*')
        .eq('client_id', client.id)
        .gte('date', startStr)
        .order('date'),
      supabase
        .from('daily_logs')
        .select('*')
        .eq('client_id', client.id)
        .gte('date', startStr)
        .order('date'),
      supabase
        .from('protocol_events')
        .select('*')
        .eq('client_id', client.id)
        .gte('date', startStr)
        .order('date'),
    ]);

    const items = itemsRes.data || [];
    const checkoffs = checkoffsRes.data || [];
    const logs = logsRes.data || [];
    const events = eventsRes.data || [];

    setProtocolEvents(events);

    const totalItems = items.length;

    // Build daily timeline data (Chart 1 + Chart 3)
    const dayCount = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
    const daily = [];
    const wellness = [];

    for (let i = 0; i < dayCount; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      const shortDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Compliance for this day
      const dayCheckoffs = checkoffs.filter((c) => c.date === dateStr);
      const compliance = totalItems > 0 ? Math.round((dayCheckoffs.length / totalItems) * 100) : 0;

      // Wellness for this day
      const dayLog = logs.find((l) => l.date === dateStr);
      const enabledSymptoms = client.enabled_symptoms || DEFAULT_ENABLED_SYMPTOMS;
      const wellnessScore = dayLog ? calculateWellnessScore(dayLog, enabledSymptoms) : null;

      daily.push({
        date: shortDate,
        fullDate: dateStr,
        compliance,
        wellness: wellnessScore != null ? Math.round(wellnessScore * 10) : null, // Convert 0-10 to 0-100 scale
      });

      // Build wellness data with all symptom keys
      const wellnessPoint = { date: shortDate, fullDate: dateStr };
      for (const symptom of SYMPTOM_CATALOG) {
        wellnessPoint[symptom.key] = dayLog?.[symptom.key] ?? null;
      }
      wellness.push(wellnessPoint);
    }

    setDailyData(daily);
    setWellnessData(wellness);

    // Category breakdown (Chart 2)
    const catBreakdown = PROTOCOL_CATEGORIES.map((cat) => {
      const catItems = items.filter((i) => i.category === cat.key);
      const catItemIds = new Set(catItems.map((i) => i.id));
      const catCheckoffs = checkoffs.filter((c) => catItemIds.has(c.protocol_item_id));
      const expected = catItems.length * dayCount;
      const rate = expected > 0 ? Math.min(100, Math.round((catCheckoffs.length / expected) * 100)) : 0;
      return {
        category: cat.label,
        icon: cat.icon,
        rate,
        fill: rate >= 75 ? '#3D7A5E' : rate >= 50 ? '#C2662D' : '#B93131',
      };
    }).filter((c) => c.rate > 0 || items.some((i) => PROTOCOL_CATEGORIES.find((p) => p.label === c.category)?.key === i.category));

    setCategoryData(catBreakdown.filter((c) => items.some((i) => i.category === PROTOCOL_CATEGORIES.find((p) => p.label === c.category)?.key)));
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading your progress...
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-text-secondary)' }}>
          <p>No data yet. Start logging to see your progress.</p>
        </div>
      </div>
    );
  }

  const hasData = dailyData.some((d) => d.compliance > 0 || d.wellness !== null);

  return (
    <div className="screen container progress-screen">
      <h1 className="progress-title">My Progress</h1>
      <p className="progress-tagline">See how following your protocol correlates with how you feel.</p>

      {/* Timeframe toggle */}
      <div className="progress-timeframe">
        {CHART_TIMEFRAMES.map((tf) => (
          <button
            key={tf.value}
            className={`tf-btn ${timeframe === tf.value ? 'tf-active' : ''}`}
            onClick={() => setTimeframe(tf.value)}
          >
            {tf.label}
          </button>
        ))}
      </div>

      {!hasData ? (
        <div className="progress-empty">
          <p>Not enough data yet. Keep checking off tasks and logging daily — your charts will appear here.</p>
        </div>
      ) : (
        <div className="progress-charts">
          {/* Chart 1: Compliance vs Wellness */}
          <div className="chart-section">
            <h3 className="chart-title">Compliance vs. How You Feel</h3>
            <p className="chart-desc">When compliance goes up, do you feel better?</p>
            <ComplianceWellnessChart data={dailyData} />
          </div>

          {/* Chart 2: Category Breakdown */}
          {categoryData.length > 0 && (
            <div className="chart-section">
              <h3 className="chart-title">By Category</h3>
              <p className="chart-desc">Which parts of your protocol are you nailing?</p>
              <CategoryBreakdownChart data={categoryData} />
            </div>
          )}

          {/* Chart 3: Symptom Trends */}
          <div className="chart-section">
            <h3 className="chart-title">Symptom Trends</h3>
            <p className="chart-desc">How each symptom is tracking over time.</p>
            <WellnessTrendsChart
              data={wellnessData}
              events={protocolEvents}
              enabledSymptoms={client.enabled_symptoms}
            />
          </div>
        </div>
      )}
    </div>
  );
}
